import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import "core-js/modules/es.object.keys.js";
import { useContext, useEffect, useState } from 'react';
import useIsElementInView from '../useIsElementInView';
import { SituationalAwarenessContext } from '../useCurrentScreenContexts';
var VISIBILITY_DEBOUNCE_MS = 300;
/**
 * Registers a screen context descriptor for AI agents while the returned
 * ref's element is scrolled into view, and unregisters it when it leaves
 * the viewport or unmounts. This is how scroll position feeds situational
 * awareness, e.g. "the user is looking at this skill card".
 *
 * No-op outside a SituationalAwarenessProvider, so instrumented components
 * stay usable in apps and stories without one.
 *
 * @param descriptor - What the element shows, or `null` to register nothing
 * @param options - Debounce and visibility-root overrides
 * @param options.debounceMs - How long the element must stay (in)visible
 * before the registration changes
 * @param options.rootRef - Parent to check visibility against
 * @returns A callback ref to attach to the element to watch
 */
function useVisibleScreenContext(descriptor) {
  var _ref = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {},
    _ref$debounceMs = _ref.debounceMs,
    debounceMs = _ref$debounceMs === void 0 ? VISIBILITY_DEBOUNCE_MS : _ref$debounceMs,
    rootRef = _ref.rootRef;
  var registry = useContext(SituationalAwarenessContext);
  var _useIsElementInView = useIsElementInView({
      rootRef: rootRef
    }),
    isInView = _useIsElementInView.isInView,
    watchedElementRef = _useIsElementInView.watchedElementRef;
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    isVisible = _useState2[0],
    setIsVisible = _useState2[1];
  useEffect(function () {
    if (isInView === isVisible) {
      return undefined;
    }
    var timerId = setTimeout(function () {
      return setIsVisible(isInView);
    }, debounceMs);
    return function () {
      return clearTimeout(timerId);
    };
  }, [isInView, isVisible, debounceMs]);

  // Serialised so the effect re-registers on value change, not identity
  // change. Descriptors must be JSON-serialisable anyway — they are sent
  // to the agent.
  var serialisedDescriptor = isVisible && descriptor ? JSON.stringify(descriptor) : null;
  useEffect(function () {
    if (!registry || !serialisedDescriptor) {
      return undefined;
    }
    return registry.register(JSON.parse(serialisedDescriptor));
  }, [registry, serialisedDescriptor]);
  return watchedElementRef;
}
export default useVisibleScreenContext;